import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Link, Navigate, useNavigate} from 'react-router-dom';
import {readAccessToken, SESSION_KEY} from '../auth/session';
import {RSS_ADD_POST_URL, RSS_GET_POSTS_URL, rssDeletePostUrl} from '../constants';
import {resolvePostImageSrc} from '../posts/imageUrl';
import {loadPosts, savePosts, type UserPost} from '../posts/storage';

function hasAuthSession(): boolean {
  try {
    return Boolean(sessionStorage.getItem(SESSION_KEY));
  } catch {
    return false;
  }
}

const formatPostDate = (iso: string): string => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const defaultDateTimeLocal = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function parseHttpErrorBody(text: string, status: number): string {
  let msg = `Request failed (${status})`;
  if (!text) return msg;
  try {
    const errBody = JSON.parse(text) as {message?: string | string[]};
    if (typeof errBody.message === 'string') return errBody.message;
    if (Array.isArray(errBody.message)) return errBody.message.join(', ');
  } catch {
    /* use slice below */
  }
  return text.slice(0, 200);
}

const Posts = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [badImg, setBadImg] = useState<Record<string, boolean>>({});
  const [posts, setPosts] = useState<UserPost[]>(() => loadPosts());
  const [postsLoading, setPostsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [author, setAuthor] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [pubDateInput, setPubDateInput] = useState(defaultDateTimeLocal);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const resetAddForm = () => {
    setImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setImageFile(null);
    setAuthor('');
    setTitle('');
    setDescription('');
    setPubDateInput(defaultDateTimeLocal());
    setSubmitError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openModal = () => {
    resetAddForm();
    setAddOpen(true);
  };

  const closeModal = () => {
    setAddOpen(false);
    resetAddForm();
  };

  const onPickImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    const f = e.target.files?.[0];
    if (!f) {
      setImageFile(null);
      return;
    }
    if (!f.type.startsWith('image/')) {
      setImageFile(null);
      e.target.value = '';
      return;
    }
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
  };

  const persist = (next: UserPost[]) => {
    setPosts(next);
    savePosts(next);
  };

  useEffect(() => {
    if (!hasAuthSession()) {
      setPostsLoading(false);
      return;
    }
    let cancelled = false;

    const run = async () => {
      const token = readAccessToken();
      if (!token) {
        if (!cancelled) {
          setFetchError('Your session expired. Sign in again.');
          setPostsLoading(false);
        }
        return;
      }
      if (!cancelled) setFetchError(null);
      try {
        const res = await fetch(RSS_GET_POSTS_URL, {
          headers: {Authorization: `Bearer ${token}`},
        });
        const text = await res.text();
        if (!res.ok) {
          if (!cancelled) {
            setFetchError(parseHttpErrorBody(text, res.status));
          }
          return;
        }
        let json: {data?: Array<Record<string, unknown>>};
        try {
          json = JSON.parse(text) as {data?: Array<Record<string, unknown>>};
        } catch {
          if (!cancelled) setFetchError('Invalid response from server.');
          return;
        }
        const rows = Array.isArray(json.data) ? json.data : [];
        const mapped: UserPost[] = rows.map((row) => ({
          id: String(row.id ?? ''),
          author: typeof row.author === 'string' ? row.author : undefined,
          title: typeof row.title === 'string' ? row.title : '',
          description: typeof row.description === 'string' ? row.description : '',
          pubDate: typeof row.pubDate === 'string' ? row.pubDate : '',
          image: resolvePostImageSrc(typeof row.image === 'string' ? row.image : ''),
        }));
        if (!cancelled) {
          setPosts(mapped);
          savePosts(mapped);
        }
      } catch {
        if (!cancelled) setFetchError('Network error. Could not load posts.');
      } finally {
        if (!cancelled) setPostsLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  const addPost = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!imageFile) return;
    const a = author.trim();
    const t = title.trim();
    const desc = description.trim();
    if (!t || !desc) return;

    const token = readAccessToken();
    if (!token) {
      setSubmitError('Your session expired. Sign in again.');
      return;
    }

    const fd = new FormData();
    fd.append('post_file', imageFile);
    if (a) fd.append('author', a);
    fd.append('subject', t);
    fd.append('body', desc);
    fd.append('pub_date', new Date(pubDateInput).toISOString());

    setSubmitBusy(true);
    try {
      const res = await fetch(RSS_ADD_POST_URL, {
        method: 'POST',
        headers: {Authorization: `Bearer ${token}`},
        body: fd,
      });
      if (!res.ok) {
        const text = await res.text();
        let msg = `Request failed (${res.status})`;
        if (text) {
          try {
            const errBody = JSON.parse(text) as {message?: string | string[]};
            if (typeof errBody.message === 'string') msg = errBody.message;
            else if (Array.isArray(errBody.message)) msg = errBody.message.join(', ');
            else msg = text.slice(0, 200);
          } catch {
            msg = text.slice(0, 200);
          }
        }
        setSubmitError(msg);
        return;
      }
      const json = (await res.json()) as {
        post?: {
          id: string;
          author?: string;
          title: string;
          description: string;
          pubDate: string;
          image: string;
        };
      };
      const p = json.post;
      if (!p) {
        setSubmitError('Unexpected response from server.');
        return;
      }
      const userPost: UserPost = {
        id: p.id,
        author: typeof p.author === 'string' ? p.author : undefined,
        title: p.title,
        description: p.description,
        pubDate: p.pubDate,
        image: resolvePostImageSrc(p.image),
      };
      persist([userPost, ...posts]);
      closeModal();
    } catch {
      setSubmitError('Network error. Try again.');
    } finally {
      setSubmitBusy(false);
    }
  };

  /** Numeric id = saved on server; others = local-only draft, remove from storage only. */
  const remove = async (id: string) => {
    setDeleteError(null);
    const serverId = /^\d+$/.test(id.trim());

    if (!serverId) {
      persist(posts.filter((p) => p.id !== id));
      return;
    }

    const token = readAccessToken();
    if (!token) {
      setDeleteError('Your session expired. Sign in again.');
      return;
    }

    setDeletingId(id);
    try {
      const res = await fetch(rssDeletePostUrl(id), {
        method: 'DELETE',
        headers: {Authorization: `Bearer ${token}`},
      });
      if (res.ok) {
        persist(posts.filter((p) => p.id !== id));
        return;
      }
      const text = await res.text();
      if (res.status === 404) {
        persist(posts.filter((p) => p.id !== id));
        return;
      }
      setDeleteError(parseHttpErrorBody(text, res.status));
    } catch {
      setDeleteError('Network error. Try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const sorted = useMemo(() => {
    return [...posts].sort(
      (a, b) => new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime()
    );
  }, [posts]);

  if (!hasAuthSession()) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="posts-page">
      <div className="posts-inner">
        <header className="posts-top">
          <button type="button" className="posts-back" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <Link to="/" className="posts-home-link">
            CriticalMinerals.News
          </Link>
          <button type="button" className="posts-add-btn" onClick={openModal}>
            Add Post
          </button>
        </header>

        <h1 className="posts-title">Posts</h1>

        {fetchError ? <p className="posts-delete-banner">{fetchError}</p> : null}
        {deleteError ? <p className="posts-delete-banner">{deleteError}</p> : null}

        <ul className="posts-list">
          {postsLoading && sorted.length === 0 ? (
            <li className="posts-empty">Loading posts…</li>
          ) : sorted.length === 0 ? (
            <li className="posts-empty">No posts yet. Click &quot;Add Post&quot; to create one.</li>
          ) : (
            sorted.map((p) => (
              <li key={p.id} className="posts-item">
                <div className="posts-item-img-wrap">
                  {badImg[p.id] ? (
                    <div className="posts-item-img-fallback" aria-hidden>
                      Image unavailable
                    </div>
                  ) : (
                    <img
                      className="posts-item-img"
                      src={resolvePostImageSrc(p.image)}
                      alt=""
                      loading="lazy"
                      onError={() => setBadImg((m) => ({...m, [p.id]: true}))}
                    />
                  )}
                </div>
                <div className="posts-item-body">
                  <h2 className="posts-item-title">{p.title || 'Untitled'}</h2>
                  {p.author ? <div className="posts-item-author">{p.author}</div> : null}
                  <p className="posts-item-desc">{p.description}</p>
                  <time className="posts-item-date" dateTime={p.pubDate}>
                    {formatPostDate(p.pubDate)}
                  </time>
                </div>
                <button
                  type="button"
                  className="posts-item-del"
                  onClick={() => void remove(p.id)}
                  disabled={deletingId !== null}
                  aria-label="Delete post"
                >
                  {deletingId === p.id ? 'Deleting…' : 'Delete'}
                </button>
              </li>
            ))
          )}
        </ul>
      </div>

      {addOpen ? (
        <div className="posts-modal-overlay" role="presentation" onClick={closeModal}>
          <div
            className="posts-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="posts-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="posts-modal-title" className="posts-modal-title">
              New post
            </h2>
            <form className="posts-form" onSubmit={addPost}>
              <label className="posts-field">
                Image
                <input
                  ref={fileInputRef}
                  className="posts-file"
                  type="file"
                  accept="image/*"
                  onChange={onPickImage}
                  disabled={submitBusy}
                  required
                />
              </label>
              {imagePreview ? (
                <div className="posts-image-preview">
                  <img src={imagePreview} alt="" />
                </div>
              ) : null}
              <label className="posts-field">
                Author
                <input
                  className="posts-input"
                  type="text"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder="Author name (optional)"
                  disabled={submitBusy}
                />
              </label>
              <label className="posts-field">
                Title
                <input
                  className="posts-input"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Post title"
                  disabled={submitBusy}
                  required
                />
              </label>
              <label className="posts-field">
                Description
                <textarea
                  className="posts-textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  placeholder="…"
                  disabled={submitBusy}
                  required
                />
              </label>
              <label className="posts-field">
                Publication date
                <input
                  className="posts-input"
                  type="datetime-local"
                  value={pubDateInput}
                  onChange={(e) => setPubDateInput(e.target.value)}
                  disabled={submitBusy}
                  required
                />
              </label>
              {submitError ? <p className="posts-submit-error">{submitError}</p> : null}
              <div className="posts-modal-actions">
                <button
                  type="button"
                  className="posts-btn-secondary"
                  onClick={closeModal}
                  disabled={submitBusy}
                >
                  Cancel
                </button>
                <button type="submit" className="posts-btn-primary" disabled={submitBusy}>
                  {submitBusy ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Posts;
